import { egmCardBank, isInsightCasinoSchema } from 'services/insightCasino';
import { ICasinoSchema } from 'appRedux/models/casinoModels';

/**
 * Insight casinos (truthy casino.insightInstance) get name / theme / section /
 * bank server-side in GetMyPlay (Mongo machines). They must skip the
 * GetSearchData catalog join. Routing is by insightInstance only.
 */
export const isMyPlayInsight = (casinoSchema?: Partial<ICasinoSchema> | null): boolean =>
  isInsightCasinoSchema(casinoSchema);

/**
 * Text after "Bank " on My Play cards.
 * Insight: `${section}${bank}` (same as egmCardBank for insight rows).
 * Non-insight: section, unchanged.
 */
export const myPlayBankText = (
  item: { section?: string | null; bank?: string | null },
  isInsight: boolean
): string =>
  isInsight
    ? egmCardBank({ isInsightRow: true, section: item.section, bank: item.bank })
    : String(item.section ?? '');

/**
 * Most Played Titles donut: a title with a blank name (no machine match) rolls
 * into the 'Other' slice, like production. Its spins are added to the existing
 * 'Other' row, or a new 'Other' row is appended at the end. Order of named
 * rows is preserved; mnum is never used as a label and nothing is 'Unknown'.
 */
export const foldBlankTitlesIntoOther = <T extends { name: string; spins: number }>(
  rows: T[]
): Array<{ name: string; spins: number }> => {
  let blankSpins = 0;
  let hasBlank = false;
  const out: Array<{ name: string; spins: number }> = [];
  rows.forEach((r) => {
    const name = String(r.name ?? '').trim();
    if (!name) {
      hasBlank = true;
      blankSpins += Number(r.spins) || 0;
    } else {
      out.push({ name: r.name, spins: r.spins });
    }
  });
  if (!hasBlank) return out;
  const otherIdx = out.findIndex((r) => r.name.trim() === 'Other');
  if (otherIdx >= 0) {
    // eslint-disable-next-line security/detect-object-injection
    out[otherIdx] = { ...out[otherIdx], spins: out[otherIdx].spins + blankSpins };
  } else {
    out.push({ name: 'Other', spins: blankSpins });
  }
  return out;
};

/**
 * True when the card should render the '·' separator and the "Bank …" label.
 * Both are hidden together when the bank text is empty.
 */
export const hasMyPlayBankText = (
  item: { section?: string | null; bank?: string | null },
  isInsight: boolean
): boolean => myPlayBankText(item, isInsight) !== '';
